import { useCallback, useEffect, useRef, useState } from 'react';

export type ApproveAlertStepId = 'forecast' | 'consensus' | 'aggregates';
export type ApproveAlertStepStatus = 'pending' | 'running' | 'done' | 'error';

export interface ApproveAlertStep {
  id: ApproveAlertStepId;
  status: ApproveAlertStepStatus;
}

const STEP_IDS: ApproveAlertStepId[] = ['forecast', 'consensus', 'aggregates'];
const STEP_ADVANCE_MS = 700;

function initialSteps(): ApproveAlertStep[] {
  return STEP_IDS.map((id) => ({ id, status: 'pending' as const }));
}

function setStepStatus(
  steps: ApproveAlertStep[],
  id: ApproveAlertStepId,
  status: ApproveAlertStepStatus,
): ApproveAlertStep[] {
  return steps.map((step) => (step.id === id ? { ...step, status } : step));
}

function markAllDone(steps: ApproveAlertStep[]): ApproveAlertStep[] {
  return steps.map((step) => ({ ...step, status: 'done' as const }));
}

function markAllError(steps: ApproveAlertStep[]): ApproveAlertStep[] {
  return steps.map((step) =>
    step.status === 'done' ? step : { ...step, status: 'error' as const },
  );
}

export function useApproveAlertProgressSteps() {
  const [steps, setSteps] = useState<ApproveAlertStep[]>(initialSteps);
  const [isApplying, setIsApplying] = useState(false);
  const timersRef = useRef<number[]>([]);

  const clearTimers = useCallback(() => {
    timersRef.current.forEach((timerId) => window.clearTimeout(timerId));
    timersRef.current = [];
  }, []);

  useEffect(() => clearTimers, [clearTimers]);

  const resetSteps = useCallback(() => {
    clearTimers();
    setSteps(initialSteps());
    setIsApplying(false);
  }, [clearTimers]);

  const beginApplying = useCallback(() => {
    clearTimers();
    setIsApplying(true);
    let next = initialSteps();
    next = setStepStatus(next, 'forecast', 'running');
    setSteps(next);

    const consensusTimer = window.setTimeout(() => {
      setSteps((current) => {
        let updated = setStepStatus(current, 'forecast', 'done');
        updated = setStepStatus(updated, 'consensus', 'running');
        return updated;
      });
    }, STEP_ADVANCE_MS);
    timersRef.current.push(consensusTimer);

    const aggregatesTimer = window.setTimeout(() => {
      setSteps((current) => {
        let updated = setStepStatus(current, 'consensus', 'done');
        updated = setStepStatus(updated, 'aggregates', 'running');
        return updated;
      });
    }, STEP_ADVANCE_MS * 2);
    timersRef.current.push(aggregatesTimer);
  }, [clearTimers]);

  const completeApplying = useCallback(() => {
    clearTimers();
    setSteps(markAllDone(initialSteps()));
    setIsApplying(false);
  }, [clearTimers]);

  const failApplying = useCallback(() => {
    clearTimers();
    setSteps((current) => markAllError(current));
    setIsApplying(false);
  }, [clearTimers]);

  return {
    steps,
    isApplying,
    beginApplying,
    completeApplying,
    failApplying,
    resetSteps,
  };
}
